/**
 * 职责：浏览器 EventSource 封装，解码 meta/agentx 命名帧并校验协议版本。
 * 链路位置：业务页面 createAgentxSseSource({ url }) → AgentSession source prop。
 *
 * 错误分层（对齐 claude-code SSETransport / Messages 管线）：
 * - 传输 terminal：握手前/未 finish 断连 → reject(SseTransportTerminalError)，不进 Timeline
 * - 传输 benign：finish/error 帧后的 EventSource.onerror → resolve
 * - 业务层：agentx event_type=error → reducer → ErrorNode（唯一用户可见错误文案）
 */
import type { LangchainAgentEvent } from '../types/events';
import type { Source, SourceHandler, SourceEventContext } from './types';
import type { ApplicationKind } from './applicationKind';
import {
  ApplicationKindMismatchError,
  MissingApplicationKindError,
} from './applicationKind';

export const EXPECTED_PROTOCOL_VERSION = '1';

/** 传输层 terminal 失败（须排查/fix）；与 agentx error 帧（业务错误）分离。 */
export type SseTransportFailureKind = 'handshake_failed' | 'premature_close';

export class SseTransportTerminalError extends Error {
  readonly kind: SseTransportFailureKind;

  constructor(kind: SseTransportFailureKind, message: string) {
    super(message);
    this.name = 'SseTransportTerminalError';
    this.kind = kind;
  }
}

export interface AgentxSseSourceOptions {
  url: string;
  autoReconnect?: boolean;
  reconnectDelayMs?: number;
  /** 每收到 agentx 帧 SSE id 时回调（含续传重叠 id） */
  onLastEventId?: (id: string) => void;
  /** 测试注入：替换全局 EventSource */
  EventSourceImpl?: typeof EventSource;
  /** /chat 与 /workflow 必填：校验 meta.application_kind */
  expectedApplicationKind?: ApplicationKind;
}

export interface AgentxMetaFrame {
  protocol_version: string;
  session_id?: string | null;
  started_at?: number;
  application_kind?: ApplicationKind;
}

/** 传输层 diagnostics；不驱动 UI（对齐 CC logForDiagnostics / warn+drop）。 */
function transportLog(message: string, level: 'warn' | 'error' = 'warn'): void {
  const line = `[langchain_agentx_stream_ui] SSE transport: ${message}`;
  if (level === 'error') {
    console.error(line);
  } else {
    console.warn(line);
  }
}

/** 手动重连时追加 resume 查询参数（EventSource 无法设 Last-Event-ID 请求头）。 */
function isSseStreamTerminalEvent(
  agentEvent: LangchainAgentEvent,
  expectedApplicationKind?: ApplicationKind,
): boolean {
  const eventType = agentEvent.event_type;
  if (eventType === 'error') return true;
  if (expectedApplicationKind === 'workflow') {
    return eventType === 'workflow-end' || eventType === 'workflow-failed';
  }
  return eventType === 'finish';
}

export function buildResumeStreamUrl(
  baseUrl: string,
  lastEventId: string | null | undefined,
  queryParam = 'last_event_id',
): string {
  if (!lastEventId) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}${encodeURIComponent(queryParam)}=${encodeURIComponent(lastEventId)}`;
}

export function createAgentxSseSource(options: AgentxSseSourceOptions): Source {
  const {
    url,
    autoReconnect = true,
    reconnectDelayMs = 1000,
    onLastEventId,
    EventSourceImpl = EventSource,
    expectedApplicationKind,
  } = options;

  return {
    start(handler: SourceHandler, signal: AbortSignal): Promise<void> {
      return new Promise((resolve, reject) => {
        let es: EventSource | null = null;
        let handshakeOk = false;
        let lastEventId: string | null = null;
        let fatal = false;
        /** finish/error 帧后服务端关连接；EventSource.onerror 为预期行为，非 UI 错误。 */
        let streamTerminal = false;

        type BufferedDelta = {
          event: LangchainAgentEvent;
          ctx?: SourceEventContext;
        };
        let bufferedDelta: BufferedDelta | null = null;
        let flushTimer: ReturnType<typeof setTimeout> | null = null;

        const flushBufferedDelta = (): void => {
          if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
          }
          if (!bufferedDelta) return;
          handler(bufferedDelta.event, bufferedDelta.ctx);
          bufferedDelta = null;
        };

        const isBufferedDeltaEvent = (event: LangchainAgentEvent): boolean =>
          event.event_type === 'text-delta' || event.event_type === 'reasoning-delta';

        const mergeDeltaEvent = (
          prev: LangchainAgentEvent,
          next: LangchainAgentEvent,
        ): LangchainAgentEvent | null => {
          if (prev.event_type !== next.event_type) return null;
          if (prev.step_index !== next.step_index) return null;
          if (prev.tool_name !== next.tool_name) return null;
          if (prev.session_id !== next.session_id) return null;

          const prevData = prev.data as { delta?: string; text?: string; content?: string };
          const nextData = next.data as { delta?: string; text?: string; content?: string };

          const prevChunk = prevData.delta ?? prevData.text ?? prevData.content ?? '';
          const nextChunk = nextData.delta ?? nextData.text ?? nextData.content ?? '';

          return {
            ...next,
            data: {
              ...next.data,
              text: `${prevChunk}${nextChunk}`,
            },
          } as LangchainAgentEvent;
        };

        const cleanup = (): void => {
          es?.close();
          es = null;
        };

        const finish = (error?: Error): void => {
          flushBufferedDelta();
          cleanup();
          if (error) reject(error);
          else resolve();
        };

        const failHandshake = (error: Error): void => {
          fatal = true;
          transportLog(error.message, 'error');
          cleanup();
          finish(error);
        };

        const failPrematureClose = (): void => {
          const error = new SseTransportTerminalError(
            'premature_close',
            'SSE connection lost before stream finished',
          );
          transportLog(error.message, 'error');
          cleanup();
          finish(error);
        };

        const connect = (resumeFromId?: string | null): void => {
          if (signal.aborted || fatal) {
            finish();
            return;
          }

          const connectUrl =
            resumeFromId != null && resumeFromId !== ''
              ? buildResumeStreamUrl(url, resumeFromId)
              : url;

          es = new EventSourceImpl(connectUrl);
          handshakeOk = false;

          es.addEventListener('meta', (event: MessageEvent<string>) => {
            try {
              const meta = JSON.parse(event.data) as AgentxMetaFrame;
              if (meta.protocol_version !== EXPECTED_PROTOCOL_VERSION) {
                failHandshake(
                  new Error(`Unsupported protocol_version: ${meta.protocol_version}`),
                );
                return;
              }
              if (expectedApplicationKind) {
                const actual = meta.application_kind;
                if (actual === undefined || actual === null) {
                  failHandshake(new MissingApplicationKindError());
                  return;
                }
                if (actual !== expectedApplicationKind) {
                  failHandshake(
                    new ApplicationKindMismatchError(expectedApplicationKind, actual),
                  );
                  return;
                }
              }
              handshakeOk = true;
            } catch (err) {
              const message =
                err instanceof Error ? err.message : 'Failed to decode meta frame';
              failHandshake(new Error(message));
            }
          });

          es.addEventListener('agentx', (event: MessageEvent<string>) => {
            if (!handshakeOk) {
              failHandshake(new Error('Received agentx frame before meta handshake'));
              return;
            }
            try {
              const agentEvent = JSON.parse(event.data) as LangchainAgentEvent;
              const sseEventId = event.lastEventId || undefined;
              if (sseEventId) {
                lastEventId = sseEventId;
                onLastEventId?.(sseEventId);
              }

              const ctx = sseEventId ? { sseEventId } : undefined;

              if (isBufferedDeltaEvent(agentEvent)) {
                if (bufferedDelta) {
                  const merged = mergeDeltaEvent(bufferedDelta.event, agentEvent);
                  if (merged) {
                    bufferedDelta = { event: merged, ctx };
                  } else {
                    flushBufferedDelta();
                    bufferedDelta = { event: agentEvent, ctx };
                  }
                } else {
                  bufferedDelta = { event: agentEvent, ctx };
                }

                if (!flushTimer) {
                  flushTimer = setTimeout(() => {
                    flushBufferedDelta();
                  }, 16);
                }
              } else {
                flushBufferedDelta();
                handler(agentEvent, ctx);
                if (isSseStreamTerminalEvent(agentEvent, expectedApplicationKind)) {
                  streamTerminal = true;
                }
              }
            } catch (err) {
              const message =
                err instanceof Error
                  ? err.message
                  : 'Failed to decode agentx frame';
              transportLog(`Dropped agentx frame: ${message}`, 'warn');
            }
          });

          es.onerror = () => {
            flushBufferedDelta();
            // 已收到 finish/error 帧后的关连接：EventSource 规范行为，非故障。
            if (signal.aborted || fatal || streamTerminal) {
              cleanup();
              finish();
              return;
            }
            // 握手前断连（含 HTTP 4xx/5xx、CORS）：EventSource 不提供 status，只能凭「尚无 meta」判定。
            if (!handshakeOk) {
              const error = new SseTransportTerminalError(
                'handshake_failed',
                'SSE connection failed before meta handshake',
              );
              transportLog(error.message, 'error');
              cleanup();
              finish(error);
              return;
            }
            if (autoReconnect) {
              cleanup();
              setTimeout(() => connect(lastEventId), reconnectDelayMs);
              return;
            }
            // 已握手但未收到 finish/error 就断连：真实传输中断，须 reject 供宿主感知。
            failPrematureClose();
          };
        };

        signal.addEventListener('abort', () => {
          flushBufferedDelta();
          fatal = true;
          finish();
        }, { once: true });

        connect();
      });
    },
  };
}

/** 测试/Story 用：内存事件源 */
export function createMockSource(events: LangchainAgentEvent[]): Source {
  return {
    async start(handler, signal) {
      for (const event of events) {
        if (signal.aborted) break;
        handler(event);
      }
    },
  };
}
